// The contract barrel, exported to the frontends as `@behuman/middleware/contracts`
// (re-exported by @behuman/core-contract). It must import ONLY `*.contract.ts` files —
// never handlers — so the web bundle never drags in @orpc/server, NestJS, or handler
// closures. Contract files are browser-safe by construction (`oc` + zod only).
import {
  authAdminOverview,
  authAdminSessions,
  authAdminRevokeSession,
  authAdminRevokeUserSessions,
  authAdminTrustedDevices,
  authAdminRevokeTrustedDevice,
  authAdminLockouts,
  authAdminClearLockout,
  authAdminChallenges,
  authAdminVoidChallenge,
  authAdminOtpOverrides,
  authAdminSetOtpOverride,
  authAdminRemoveOtpOverride,
  authAdminAuditEvents,
} from '../auth-v2/handlers/auth-admin.contract'
import { authChangePassword } from '../auth-v2/handlers/auth-change-password.contract'
import { authLogin } from '../auth-v2/handlers/auth-login.contract'
import { authMfaSend, authMfaVerify } from '../auth-v2/handlers/auth-mfa.contract'
import { authResetPassword, authSetPassword } from '../auth-v2/handlers/auth-password.contract'
import { authProfileSelect } from '../auth-v2/handlers/auth-profile-select.contract'
import { authLogout, authRefresh } from '../auth-v2/handlers/auth-session.contract'
import { healthcheck } from '../healthcheck/handlers/healthcheck.contract'
import { healthcheckUser } from '../healthcheck/handlers/healthcheck-user.contract'
import { healthcheckAdmin } from '../healthcheck/handlers/healthcheck-admin.contract'
import { assistantActors } from '../assistant/handlers/assistant-actors.contract'
import { assistantConfig } from '../assistant/handlers/assistant-config.contract'
import { assistantStart } from '../assistant/handlers/assistant-start.contract'
import { assistantList } from '../assistant/handlers/assistant-list.contract'
import { assistantGet } from '../assistant/handlers/assistant-get.contract'
import { assistantStream } from '../assistant/handlers/assistant-stream.contract'
import { assistantDelete } from '../assistant/handlers/assistant-delete.contract'
import { assistantSend } from '../assistant/handlers/assistant-send.contract'
import { assistantCancel } from '../assistant/handlers/assistant-cancel.contract'
import {
  assistantFavoriteGet,
  assistantFavoriteAdd,
  assistantFavoriteUpdate,
  assistantFavoriteDelete,
} from '../assistant/handlers/assistant-favorite.contract'
import { patientGet } from '../patient/handlers/patient.contract'
import {
  patientHealthRecordGet,
  patientHealthRecordList,
} from '../patient/handlers/patient-health-record.contract'
import {
  patientIdentityDocumentGet,
  patientIdentityDocumentUpload,
  patientProfileGet,
  patientProfileUpdate,
} from '../patient/handlers/patient-profile.contract'
import { listPatients } from '../patient/handlers/list-patients.contract'
import {
  checkIdentityVerification,
  getIdentityVerification,
  getPersonaIdImage,
  getPortalAccess,
  markIdentityVerified,
  setPersonaEnrollment,
} from '../patient/handlers/patient-identity.contract'
import { inviteOrgMember } from '../super/handlers/invite-org-member.contract'
import { getMyPractitionerOpsScope } from '../practitioner/handlers/practitioner-ops-scope.contract'
import { listPractitioners } from '../practitioner/handlers/list-practitioners.contract'
import { updatePractitionerCredentials } from '../practitioner/handlers/update-practitioner-credentials.contract'
import { getPractitioner } from '../practitioner/handlers/get-practitioner.contract'
import { getMyProfile, updateMyProfile } from '../practitioner/handlers/my-profile.contract'
import { updateMyCredentials } from '../practitioner/handlers/my-credentials.contract'
import { patientClinicalSummaryDetails } from '../patient/handlers/patient-clinical-summary-details.contract'
import { patientMeClinicalSummaryOverview } from '../patient/handlers/patient-me-clinical-summary-overview.contract'
import { patientMeMetricTrends } from '../patient/handlers/patient-me-metric-trends.contract'
import { patientMeSchedulingCoverage } from '../patient/handlers/patient-me-scheduling-coverage.contract'
import { patientMeTimeline } from '../patient/handlers/patient-me-timeline.contract'
import {
  patientClinicalSummaryOverview,
  patientClinicalSummaryOverviewRefresh,
} from '../patient/handlers/patient-clinical-summary-overview.contract'
import {
  patientRisksGaps,
  patientRisksGapsRefresh,
} from '../patient/handlers/patient-risks-gaps.contract'
import { listIncomingFaxes } from '../incoming-fax/handlers/list-incoming-faxes.contract'
import { getIncomingFax } from '../incoming-fax/handlers/get-incoming-fax.contract'
import { getIncomingFaxPatients } from '../incoming-fax/handlers/get-incoming-fax-patients.contract'
import { checkIncomingFaxPatients } from '../incoming-fax/handlers/check-incoming-fax-patients.contract'
import { reviewIncomingFaxPatients } from '../incoming-fax/handlers/review-incoming-fax-patients.contract'
import { markIncomingFaxViewed } from '../incoming-fax/handlers/mark-incoming-fax-viewed.contract'
import { deleteIncomingFax } from '../incoming-fax/handlers/delete-incoming-fax.contract'
import { triageIncomingFaxDocument } from '../incoming-fax/handlers/triage-incoming-fax-document.contract'
import { triageIncomingFaxSplit } from '../incoming-fax/handlers/triage-incoming-fax-split.contract'
import { createFaxTask } from '../fax-tasks/handlers/create-fax-task.contract'
import { detachFaxTasks } from '../fax-tasks/handlers/detach-fax-tasks.contract'
import { faxTaskSummaries } from '../fax-tasks/handlers/fax-task-summaries.contract'
import { linkOutgoingFax } from '../fax-tasks/handlers/link-outgoing-fax.contract'
import { unlinkOutgoingFax } from '../fax-tasks/handlers/unlink-outgoing-fax.contract'
import { outgoingFaxLinks } from '../fax-tasks/handlers/outgoing-fax-links.contract'
import { updateOutgoingFaxLink } from '../fax-tasks/handlers/update-outgoing-fax-link.contract'
import { acknowledgeThreadDelivery } from '../messages/handlers/acknowledge-thread-delivery.contract'
import { createOrganizationConnect } from '../organizations/handlers/create-organization-connect.contract'
import { listOrganizationConnects } from '../organizations/handlers/list-organization-connects.contract'
import { getOrganizationConnect } from '../organizations/handlers/get-organization-connect.contract'
import { acceptOrganizationConnect } from '../organizations/handlers/accept-organization-connect.contract'
import { declineOrganizationConnect } from '../organizations/handlers/decline-organization-connect.contract'
import { deleteOrganizationConnect } from '../organizations/handlers/delete-organization-connect.contract'
import { transferAppointment } from '../appointment/handlers/transfer-appointment.contract'
import { getSlotsByOrganization } from '../appointment/handlers/get-slots-by-organization.contract'
import { getFutureAppointmentsByPatient } from '../appointment/handlers/get-future-appointments-by-patient.contract'
import { getLastSeenProvider } from '../appointment/handlers/get-last-seen-provider.contract'
import { batchRemoveOrgMembers } from '../super/handlers/batch-remove-org-members.contract'
import { removeOrgMember } from '../super/handlers/remove-org-member.contract'
import { getReturningPatientStatuses } from '../appointment/handlers/get-returning-patients.contract'
import { getAppointmentTypes } from '../appointment/handlers/get-appointment-types.contract'
import { listBookableProviders } from '../appointment/handlers/list-bookable-providers.contract'
import { getProviderDaySlots } from '../appointment/handlers/get-provider-day-slots.contract'
import { listAppointmentAgenda } from '../appointment/handlers/list-appointment-agenda.contract'
import { getAppointmentReadiness } from '../appointment/handlers/get-appointment-readiness.contract'
import {
  listPatientVisits,
  cancelPatientVisit,
  patientVisitCallAccess,
  savePatientVisitLocation,
  savePatientVisitDeviceCheck,
} from '../appointment/handlers/patient-visits.contract'
import {
  listPatientCoverages,
  submitPatientInsurance,
  uploadPatientInsuranceCard,
  savePatientVisitInsurance,
} from '../insurance/handlers/patient-insurance.contract'
import { insuranceVerdicts } from '../insurance/handlers/insurance-verdicts.contract'
import { consentGenerate } from '../consent/handlers/consent-generate.contract'
import { consentSign } from '../consent/handlers/consent-sign.contract'
import { consentGet } from '../consent/handlers/consent-get.contract'
import { consentPreview } from '../consent/handlers/consent-preview.contract'
import { facilitySearch } from '../facility/handlers/facility-search.contract'
import { facilityDiscoverySearch } from '../facility-discovery/handlers/facility-discovery-search.contract'
import { facilityDiscoveryAdopt } from '../facility-discovery/handlers/facility-discovery-adopt.contract'
import { facilityDiscoveryEnrichment } from '../facility-discovery/handlers/facility-discovery-enrichment.contract'
import { facilityDiscoveryFrequent } from '../facility-discovery/handlers/facility-discovery-frequent.contract'
import { facilityDiscoveryRemember } from '../facility-discovery/handlers/facility-discovery-remember.contract'
import { facilityDiscoveryBookmark } from '../facility-discovery/handlers/facility-discovery-bookmark.contract'
import { referralDiscoveryNearby } from '../referral-discovery/handlers/referral-discovery-nearby.contract'
import { referralDiscoveryRosters } from '../referral-discovery/handlers/referral-discovery-rosters.contract'
import { referralDiscoveryDoctors } from '../referral-discovery/handlers/referral-discovery-doctors.contract'
import { referralDiscoveryFrequent } from '../referral-discovery/handlers/referral-discovery-frequent.contract'
import { referralDiscoveryResolve } from '../referral-discovery/handlers/referral-discovery-resolve.contract'
import { referralDiscoveryRemember } from '../referral-discovery/handlers/referral-discovery-remember.contract'
import { referralDiscoveryBookmark } from '../referral-discovery/handlers/referral-discovery-bookmark.contract'
import { referralDraft } from '../referrals/handlers/referral-draft.contract'
import { recordsFacilityLink } from '../facility/handlers/records-facility-link.contract'
import { recordsFacilityCreate } from '../facility/handlers/records-facility-create.contract'
import { recordLocationsGet } from '../facility/handlers/record-locations-get.contract'
import { authorizationGenerate } from '../authorization/handlers/authorization-generate.contract'
import { authorizationSign } from '../authorization/handlers/authorization-sign.contract'
import { authorizationGet } from '../authorization/handlers/authorization-get.contract'
import { getRecordLocatorEnrichment } from '../record-locator/handlers/get-record-locator-enrichment.contract'
import { runRecordLocatorEnrichment } from '../record-locator/handlers/run-record-locator-enrichment.contract'
import { refreshRecordLocatorProviders } from '../record-locator/handlers/refresh-record-locator-providers.contract'
import { scribeStartSession } from '../scribe/handlers/start-session.contract'
import { scribeAppendTranscript } from '../scribe/handlers/append-transcript.contract'
import { scribeFinalizeSession } from '../scribe/handlers/finalize-session.contract'
import { scribeGetActiveSession } from '../scribe/handlers/get-active-session.contract'
import { scribeGetSessionChunks } from '../scribe/handlers/get-session-chunks.contract'
import { scribeGenerationStatus } from '../scribe/handlers/get-generation-status.contract'
import { scribeDiscardSession } from '../scribe/handlers/discard-session.contract'
import { scribeGenerateDraft } from '../scribe/handlers/generate-draft.contract'
import { visitCallSignature } from '../appointment/handlers/call-signature.contract'
import { scribeJoinClaim, scribeJoinRelease } from '../scribe/handlers/join-claim.contract'
import { scribeRecordDuration } from '../scribe/handlers/record-duration.contract'
import { scribeDictateFormat } from '../scribe/handlers/dictate.contract'
import { visitLockProgressNote } from '../visit/handlers/lock-progress-note.contract'
import { visitGetProgressNote } from '../visit/handlers/get-progress-note.contract'
import { visitBillingGet } from '../visit/handlers/get-visit-billing.contract'
import { visitUpdateBilling } from '../visit/handlers/update-visit-billing.contract'
import { visitVideoToken } from '../visit/handlers/video-token.contract'
import { visitJoinLinkCreate } from '../visit/handlers/join-link.contract'
import { visitSubmitSoapNote, visitSaveSoapNoteDraft } from '../visit/handlers/soap-note.contract'
import { patientExtractIdPhoto } from '../patient/handlers/extract-id-photo.contract'
import { patientFindDuplicate } from '../patient/handlers/find-duplicate.contract'
import { patientEnrol } from '../user/handlers/patient-enrol.contract'
import {
  progressNoteSaveAddendum,
  progressNoteSignAddendum,
} from '../visit/handlers/progress-note-addendum.contract'
import { tefcaRetrySearch } from '../tefca/handlers/tefca-retry-search.contract'
import { tefcaManualLink } from '../tefca/handlers/tefca-manual-link.contract'
import { tefcaManualCreate } from '../tefca/handlers/tefca-manual-create.contract'
import {
  listOrganizationAgreements,
  createOrganizationAgreement,
  terminateOrganizationAgreement,
} from '../super/handlers/platform-agreements.contract'
import {
  setOrganizationStripeKeys,
  getOrganizationStripeStatus,
} from '../super/handlers/organization-stripe-keys.contract'
import {
  setOrganizationDoseSpotClinic,
  getOrganizationDoseSpotStatus,
} from '../super/handlers/organization-dosespot-clinic.contract'
import {
  getPersonaPilot,
  enrollPersonaPilotPatient,
  removePersonaPilotPatient,
} from '../super/handlers/persona-pilot.contract'
import { reconcilePersonaAttempts } from '../persona/handlers/persona-reconcile.contract'
import {
  getMyIdentityVerification,
  giveMyIdentityVerificationConsent,
  startMyIdentityVerification,
  checkMyIdentityVerification,
  requestMyManualIdentityVerification,
} from '../patient/handlers/my-identity-verification.contract'
import { setPractitionerDoseSpotClinician } from '../practitioner/handlers/practitioner-dosespot-clinician.contract'
import {
  setOrganizationBridgeKey,
  getOrganizationBridgeStatus,
} from '../super/handlers/organization-bridge-key.contract'
import { listOrganizationBridgeProviders } from '../super/handlers/organization-bridge-providers.contract'
import { setPractitionerBridgeProvider } from '../practitioner/handlers/practitioner-bridge-provider.contract'
import {
  setOrganizationStediKey,
  getOrganizationStediStatus,
} from '../super/handlers/organization-stedi-key.contract'
import { listOrganizationStediProviders } from '../practitioner/handlers/organization-stedi-providers.contract'
import { setPractitionerStediProvider } from '../practitioner/handlers/practitioner-stedi-provider.contract'
import {
  listUnmatchedPatientOrganizations,
  assignUnmatchedPatient,
} from '../super/handlers/unmatched-patient-assignment.contract'
import { doseSpotLaunch, doseSpotLaunchNotifications } from '../dosespot/handlers/launch.contract'
import { doseSpotSyncPrescriptions } from '../dosespot/handlers/sync-prescriptions.contract'
import {
  doseSpotPharmacySearch,
  doseSpotSetDefaultPharmacy,
  doseSpotRemoveDefaultPharmacy,
} from '../dosespot/handlers/pharmacy.contract'
import {
  kateSessionCreate,
  kateSessionEnd,
  kateSessionEventReport,
  kateSessionGet,
  kateSessionTurnReport,
} from '../kate/handlers/kate-session.contract'
import {
  kateCheckInGet,
  kateCheckInOpened,
  kateCheckInReview,
  kateCheckInSend,
} from '../kate/handlers/kate-checkin.contract'
import { createCopayCheckout } from '../copay/handlers/create-copay-checkout.contract'
import { getCopayStatus, syncCopayStatus } from '../copay/handlers/copay-status.contract'
import { sendCopayLink } from '../copay/handlers/send-copay-link.contract'
import { refundCopay } from '../copay/handlers/refund-copay.contract'
import { recordPatientPayment } from '../copay/handlers/record-patient-payment.contract'
import { resendCopayReceipt } from '../copay/handlers/resend-copay-receipt.contract'
import {
  patientSelfPayCheckout,
  patientSelfPayFee,
  patientSelfPayStatus,
} from '../copay/handlers/patient-self-pay.contract'
import {
  boardList,
  boardCreate,
  boardGet,
  boardRename,
  boardDelete,
  boardAddColumn,
  boardRenameColumn,
  boardReorderColumns,
  boardRemoveColumn,
} from '../boards/handlers/boards.contract'
import {
  boardItemCreate,
  boardItemUpdate,
  boardItemDelete,
  boardNoteAdd,
  boardMarkRead,
  boardItemChecklistToggle,
} from '../boards/handlers/board-items.contract'
import { patientCareTasks } from '../boards/handlers/patient-care-tasks.contract'
import {
  duplicateReviewList,
  duplicateReviewCounts,
  duplicateReviewOpen,
  duplicateReviewGet,
  duplicateReviewDecide,
  duplicateReviewForPatient,
} from '../patient-merge/handlers/duplicate-review.contract'
import {
  patientMerge,
  patientMergeGet,
  patientMergeRedirect,
  patientMergeResume,
} from '../patient-merge/handlers/patient-merge.contract'
import {
  mergedPatientList,
  patientMergeRestore,
  patientMergeSettingsGet,
  patientMergeSettingsSet,
} from '../patient-merge/handlers/merged-patients.contract'
import { briefingDay } from '../briefing/handlers/briefing-day.contract'
import { briefingItemState } from '../briefing/handlers/briefing-item-state.contract'
import { boardAssignees, boardPatientSearch } from '../boards/handlers/boards-directory.contract'
import { careTaskConfigGet, careTaskConfigSet } from '../boards/handlers/care-task-config.contract'
import {
  boardNotifyPrefsList,
  boardNotifyPrefsSet,
} from '../boards/handlers/board-notify-prefs.contract'
import { sharedTestPanelsGet } from '../order/handlers/shared-panels.contract'
import { orderFaxStatus } from '../order/handlers/order-fax-status.contract'
import { seedSharedPanels } from '../super/handlers/seed-shared-panels.contract'
import {
  notificationList,
  notificationMarkRead,
  notificationDelete,
  notificationMarkAllRead,
} from '../notifications/handlers/notifications.contract'
import { billingSummaries } from '../billing/handlers/billing-summaries.contract'
import { billingDetail } from '../billing/handlers/billing-detail.contract'
import { billingSave } from '../billing/handlers/billing-save.contract'
import { billingQuoteCharge } from '../encounter-billing/handlers/billing-quote-charge.contract'
import { billingIcd10Billability } from '../encounter-billing/handlers/icd10-billability.contract'
import {
  billingFeePolicyGet,
  superSetFeePolicy,
  superSeedFeeSchedule,
} from '../encounter-billing/handlers/fee-policy.contract'
import { billingV2CaseByEncounter } from '../billing-v2/handlers/get-case-by-encounter.contract'
import { billingV2UpdateCase } from '../billing-v2/handlers/update-case.contract'
import { billingV2SyncCase } from '../billing-v2/handlers/sync-case.contract'
import { billingV2ListCases } from '../billing-v2/handlers/list-cases.contract'
import { billingV2CaseDetail } from '../billing-v2/handlers/case-detail.contract'
import { billingV2UploadEob } from '../billing-v2/handlers/upload-eob.contract'
import { billingV2RecordPayment } from '../billing-v2/handlers/record-payment.contract'
import { billingV2VccContext } from '../billing-v2/handlers/vcc-context.contract'
import { billingV2VccCharge } from '../billing-v2/handlers/vcc-charge.contract'
import {
  billingSettingsStatus,
  billingSettingsUpdate,
} from '../billing-v2/handlers/billing-settings.contract'
import { billingV2SubmitClaim } from '../billing-v2/handlers/submit-claim.contract'
import { billingV2SetRoute } from '../billing-v2/handlers/set-route.contract'
import {
  billingV2Report,
  billingV2ReportExport,
  billingV2ReportFilterOptions,
} from '../billing-v2/handlers/billing-report.contract'
import { boardTrashList, boardRestore, boardPurge } from '../boards/handlers/boards-trash.contract'
import {
  boardAdminList,
  boardOwnerCandidates,
  boardSetOwner,
} from '../boards/handlers/boards-admin.contract'
import { listTasks } from '../task/handlers/list-tasks.contract'
import { getTask } from '../task/handlers/get-task.contract'
import { runTask } from '../task/handlers/run-task.contract'
import { updateTask } from '../task/handlers/update-task.contract'
import { listTaskRuns } from '../task/handlers/list-task-runs.contract'
import { getTaskRun } from '../task/handlers/get-task-run.contract'
import { taskRunAction } from '../task/handlers/task-run-action.contract'
import { listCpgSources } from '../cpg/handlers/cpg-sources.contract'
import {
  activateCpgDocument,
  cpgDocumentFamilyAction,
  getCpgDocument,
  listCpgDocuments,
} from '../cpg/handlers/cpg-documents.contract'
import {
  createCpgFamily,
  listCpgFamilies,
  updateCpgFamily,
} from '../cpg/handlers/cpg-families.contract'
import {
  createCpgMeasure,
  listCpgMeasures,
  updateCpgMeasure,
} from '../cpg/handlers/cpg-measures.contract'
import {
  createCpgXwalkRule,
  deleteCpgXwalkRule,
  listCpgXwalkRules,
} from '../cpg/handlers/cpg-xwalk.contract'
import { listCpgAudit } from '../cpg/handlers/cpg-audit.contract'
import {
  acceptCpgProposalCluster,
  dismissCpgProposalCluster,
  listCpgProposalClusters,
  listCpgProposals,
} from '../cpg/handlers/cpg-proposals.contract'
import {
  populationFamilies,
  populationCohortExport,
  populationFamilyPatients,
  populationMeasurePatients,
  populationMeasures,
  populationProviders,
  populationStates,
  populationSummary,
  populationTrend,
} from '../population/handlers/population.contract'
import {
  crmLeadCreate,
  crmLeadImportPatients,
  crmLeadList,
  crmLeadRecordManual,
  crmLeadReport,
  crmLeadSetNotes,
  crmLeadSetStatus,
  crmLeadStatusBreakdown,
} from '../crm/handlers/crm.contract'
import {
  crmCampaignCreate,
  crmCampaignGet,
  crmCampaignLaunch,
  crmCampaignList,
  crmCampaignUpdate,
  crmCohortAdd,
  crmCohortClear,
  crmCohortList,
  crmCohortRemove,
  crmLeadCampaigns,
} from '../crm/handlers/crm-campaign.contract'
import {
  crmCohortAddPopulation,
  crmPopulationCohortCount,
  crmPopulationCohorts,
} from '../crm/handlers/crm-population.contract'
import {
  chartRecordActivity,
  chartRecordRemove,
  chartRecordRestore,
} from '../chart-records/handlers/chart-records.contract'
import { chartRecordsCombinedPdf } from '../chart-records/combined-pdf/combined-pdf.contract'
import {
  auditListAccessEvents,
  auditListChangeEvents,
  auditListFilterOptions,
  auditReportAction,
  auditReportView,
} from '../audit/handlers/audit.contract'
import { careJourneys } from '../care-journeys/handlers/care-journeys.contract'
import { createPatientLetter } from '../letters/handlers/create-patient-letter.contract'
import {
  letterSignatureRequestApprove,
  letterSignatureRequestCreate,
  letterSignatureRequestGet,
  letterSignatureRequestList,
  letterSigners,
} from '../letters/handlers/letter-signature-request.contract'
import { zocdocTestConnection } from '../zocdoc/handlers/zocdoc-test-connection.contract'
import { zocdocPingRead } from '../zocdoc/handlers/zocdoc-ping-read.contract'
import { zocdocConfig } from '../zocdoc/handlers/zocdoc-config.contract'
import { zocdocProviderLinks } from '../zocdoc/handlers/zocdoc-provider-links.contract'
import {
  zocdocSetSyncControls,
  zocdocSyncState,
} from '../zocdoc/handlers/zocdoc-sync-controls.contract'
import { zocdocProviderDetails } from '../zocdoc/handlers/zocdoc-provider-details.contract'
import { zocdocPractitionerCandidates } from '../zocdoc/handlers/zocdoc-practitioner-candidates.contract'
import { zocdocLinkPractitioner } from '../zocdoc/handlers/zocdoc-link-practitioner.contract'
import { zocdocUnlinkPractitioner } from '../zocdoc/handlers/zocdoc-unlink-practitioner.contract'
import { zocdocPractitionerSearch } from '../zocdoc/handlers/zocdoc-practitioner-search.contract'
import { zocdocLinkAppointment } from '../zocdoc/handlers/zocdoc-link-appointment.contract'
import { zocdocImportAppointment } from '../zocdoc/handlers/zocdoc-import-appointment.contract'
import { zocdocCancelAppointment } from '../zocdoc/handlers/zocdoc-cancel-appointment.contract'
import { zocdocConfirmAppointment } from '../zocdoc/handlers/zocdoc-confirm-appointment.contract'
import {
  zocdocRescheduleOptions,
  zocdocRescheduleAppointment,
} from '../zocdoc/handlers/zocdoc-reschedule-appointment.contract'
import { zocdocAppointmentStatus } from '../zocdoc/handlers/zocdoc-appointment-status.contract'
import { zocdocFetchAppointments } from '../zocdoc/handlers/zocdoc-appointments.contract'
import {
  zocdocSetAvailability,
  zocdocProviderSlots,
} from '../zocdoc/handlers/zocdoc-availability.contract'
import { zocdocCreateAppointment } from '../zocdoc/handlers/zocdoc-create-appointment.contract'
import { zocdocRepublishAvailability } from '../zocdoc/handlers/zocdoc-republish-availability.contract'
import { zocdocVisitReasons } from '../zocdoc/handlers/zocdoc-visit-reasons.contract'
import { patientCareJourney } from '../care-journeys/handlers/patient-care-journey.contract'
import { patientLabOrder } from '../care-journeys/handlers/patient-lab-order.contract'

// Care Journeys status vocabulary shared with the web dashboard via
// `@behuman/core-contract`, so the tab's badges and filter drawer build their
// option lists from the same values the handler classifies against.
export {
  ACTION_TYPES,
  FOLLOWUP_STATUSES,
  JOURNEY_KPIS,
  JOURNEY_STATES,
  JOURNEY_STATUS_FILTERS,
  JOURNEY_TONES,
  ORDER_STATUSES,
  VISIT_TYPES,
} from '../care-journeys/handlers/care-journeys.contract'

// Credential vocabulary shared with the frontends (the web member-credentials
// editor) via `@behuman/core-contract`. Re-exported here so the contract package
// stays the single import surface for both the oRPC routes and their value sets.
export {
  PRACTITIONER_QUALIFICATIONS,
  SPECIALTIES_BY_QUALIFICATION,
  PRACTITIONER_LANGUAGES,
  LICENSE_STATUSES,
  LICENSE_RESTRICTIONS,
  US_STATE_CODES,
  licenseRowSchema,
  npiNumberSchema,
  professionalRoleSchema,
  specialtySchema,
  formatProfessionalRole,
  languagesSchema,
  LICENSES_NEED_PROFESSIONAL_ROLE_MESSAGE,
  practitionerPhotoSchema,
  liabilityCoverageSchema,
  CAQH_PROFILE_STATUSES,
  caqhProfileSchema,
  ENROLLMENT_STATUSES,
  payerEnrollmentRowSchema,
  payerEnrollmentRowInputSchema,
  collectCredentialIssues,
  collectLiabilityIssues,
  collectCaqhIssues,
  collectPayerEnrollmentIssues,
  sameLicenseSets,
} from '../practitioner/handlers/member-credentials.contract'
export type {
  LicenseRow,
  LicenseDocument,
  PractitionerPhoto,
  LiabilityCoverage,
  CaqhProfile,
  CaqhProfileStatus,
  PayerEnrollmentRow,
  PayerEnrollmentRowInput,
  EnrollmentStatus,
  LicenseStatus,
  LicenseRestriction,
  LanguageCode,
  USStateCode,
  MemberCredentials,
} from '../practitioner/handlers/member-credentials.contract'
export { doseSpotClinicianIdFieldSchema } from '../practitioner/handlers/practitioner-dosespot-clinician.contract'
export { bridgeProviderIdFieldSchema } from '../practitioner/handlers/practitioner-bridge-provider.contract'
export { stediProviderIdFieldSchema } from '../practitioner/handlers/practitioner-stedi-provider.contract'
export type { DoseSpotClinicianKindDto } from '../practitioner/handlers/practitioner-dosespot-clinician.contract'
export { DOSESPOT_LAUNCH_ERRORS } from '../dosespot/handlers/launch.contract'
export type { DoseSpotLaunchErrorCode } from '../dosespot/handlers/launch.contract'

export type { TefcaRetrySearchCandidate } from '../tefca/handlers/tefca-retry-search.contract'

// Patient duplicate review & merge DTOs (PD-1986) shared with the web practitioner console.
export type {
  Comparison,
  ComparisonRecord,
  DuplicateReviewSummary,
  MergeStatus,
  RecordSignals,
  RecordSummary,
  ReviewStatus,
} from '../patient-merge/handlers/patient-merge-shared.contract'

// Progress-note SOAP payload + AI-draft types shared with the frontends (the
// web `draftToSoap` adapter and scribe finalize flow) via `@behuman/core-contract`.
export type {
  SoapNote,
  Coding,
  Condition,
  Allergy,
  ServiceRequest,
  Treatment,
  BillingEntry,
  BillingSuggestion,
} from '../visit/soap-note-payload.contract'
export type { AiAllergy, GenerateDraftResponse } from '../scribe/draft-response.contract'
export { lowConfidenceKey } from '../scribe/draft-response.contract'
// PD-1600 — the encounter-language vocabulary the call rail, the note editor
// and both portals' disclosure render from.
export {
  SCRIBE_SOURCE_LANGUAGES,
  SCRIBE_LANGUAGE_LABELS,
  scribeSourceLanguageSchema,
  isScribeSourceLanguage,
  encounterLanguageDisclosure,
} from '../scribe/handlers/encounter-language.contract'
export type { ScribeSourceLanguage } from '../scribe/handlers/encounter-language.contract'
export type {
  GetProgressNoteResponse,
  PreviousPlan,
  ProgressNote,
  ProgressNoteAddendum,
} from '../visit/handlers/get-progress-note.contract'
export type { VisitBillingResponse } from '../visit/handlers/get-visit-billing.contract'
export type { VisitJoinLinkResponse } from '../visit/handlers/join-link.contract'

// Chart-audit types shared with the web Audit section via `@behuman/core-contract`.
export type {
  AuditAccessEvent,
  AuditPageWindow,
  AuditAccessResult,
  AuditAccessSource,
  AuditAccessType,
  AuditAction,
  AuditChangeEvent,
  AuditChangeSource,
} from '../audit/handlers/audit.contract'
// The access-source vocabulary itself, so the web's badge/filter labels can be asserted
// against the contract rather than duplicating the list (S21). Same value-export pattern as
// the CPG and Boards vocabularies below.
export {
  CONSENT_FIELD_KEYS,
  CONSENT_REQUIRED_FIELDS,
} from '../consent/handlers/consent-sign.contract'
export { consentFieldBoxSchema } from '../consent/handlers/consent-preview.contract'
export type { ConsentFieldKey } from '../consent/handlers/consent-sign.contract'
export { AUDIT_ACCESS_SOURCE_VALUES } from '../audit/handlers/audit.contract'

/**
 * The chart sections whose resource types back Change History, exported so `web` can hold
 * this map against its own section registry instead of keeping a hand-copy of it.
 *
 * QA round 2 found four chart sections (care-coordination notes, procedures, immunizations,
 * family history) whose changes were never derived, because this mapping lives in middleware
 * while the registry that decides what a section contains lives in `web`, and nothing
 * compared them. `auditChangeCoverage.test.ts` now does, so adding a section without
 * deciding its audit story fails the build. A real import and not a mirror: a mirror is a
 * copy, and a copy is what drifted.
 *
 * Safe to bundle — `section-resource-types.ts` imports only FHIR types and one string
 * constant, with no Nest or server runtime behind it.
 */
export { SECTION_RESOURCE_TYPES } from '../audit/handlers/audit.contract'

// Zocdoc's cancellation-reason vocabulary, shared with the web Zocdoc dashboard's cancel modal
// via `@behuman/core-contract` (same pattern as the credential vocabulary above). Zocdoc's own
// values, so the picker cannot drift from what the API accepts.
export {
  ZOCDOC_CANCELLATION_REASON_TYPES,
  ZOCDOC_REASON_TYPES_NEEDING_TEXT,
} from '../zocdoc/handlers/zocdoc-cancel-appointment.contract'
export type { ZocdocCancellationReasonType } from '../zocdoc/handlers/zocdoc-cancel-appointment.contract'

// Platform-clinic agreement vocabulary shared with the web organization editor
// via `@behuman/core-contract` (same pattern as the credential vocabulary above).
export {
  INSURANCE_CATEGORIES,
  PLAN_TYPES_BY_CATEGORY,
  planTypesFor,
} from '../super/handlers/platform-agreements.contract'
export type {
  InsuranceCategory,
  InsurancePlanType,
  AgreementRow,
} from '../super/handlers/platform-agreements.contract'

// Boards status/priority value sets shared with the web cells.
export {
  BOARD_STATUS_VALUES,
  BOARD_PRIORITY_VALUES,
} from '../boards/handlers/boards-shared.contract'

// Population Health analytics filter vocabularies shared with the frontends (PD-1715).
export {
  POPULATION_PERIODS,
  POPULATION_COMPARISONS,
  POPULATION_MAX_CUSTOM_DAYS,
  POPULATION_AGE_GROUPS,
  POPULATION_RISK_LEVELS,
  POPULATION_KINDS,
  POPULATION_MEASURE_STATUSES,
} from '../population/handlers/population.contract'
export type {
  PopulationPeriod,
  PopulationComparison,
  PopulationAgeGroup,
  PopulationRiskLevel,
  PopulationKind,
} from '../population/handlers/population.contract'

// CRM vocabularies shared with the frontends (PD-1993).
export {
  CRM_LEAD_STATUSES,
  CRM_LEAD_SOURCES,
  CRM_CAPTURE_METHODS,
  CRM_CAMPAIGN_CHANNELS,
  CRM_CAMPAIGN_STATUSES,
  CRM_DELIVERY_STATUSES,
  CRM_SKIP_REASONS,
  CRM_GENDERS,
  CRM_NAME_PATTERN,
  CRM_POSTAL_CODE_PATTERN,
} from '../crm/handlers/crm.contract'
export type {
  CrmAttribution,
  CrmGender,
  CrmLeadStatus,
  CrmLeadSource,
  CrmCaptureMethod,
  CrmCampaignChannel,
  CrmCampaignStatus,
  CrmDeliveryStatus,
  CrmSkipReason,
} from '../crm/handlers/crm.contract'
export {
  CRM_COHORT_ADD_LIMIT,
  CRM_FIRST_NAME_TOKEN,
  CRM_SMS_MAX_LENGTH,
  CRM_SMS_OPT_OUT,
  crmSmsMaxLength,
} from '../crm/handlers/crm-campaign.contract'

// CPG registry vocabulary shared with the web Super Admin → CPG views.
export {
  CPG_DOCUMENT_STATUS_VALUES,
  CPG_UNIT_KIND_VALUES,
  CPG_XWALK_KIND_VALUES,
  CPG_XWALK_SYSTEMS,
  CPG_MEASURE_KIND_VALUES,
  CPG_MEASURE_STATUS_VALUES,
  CPG_PROPOSAL_CLUSTER_STATUS_VALUES,
} from '../cpg/handlers/cpg-registry.contract'
export type {
  CpgDocumentStatus,
  CpgUnitKind,
  CpgXwalkKind,
  CpgMeasureKind,
  CpgMeasureStatus,
  CpgProposalClusterStatus,
} from '../cpg/handlers/cpg-registry.contract'
export { CPG_DOCUMENT_QUERY_FIELDS } from '../cpg/handlers/cpg-documents.contract'
export { CPG_XWALK_QUERY_FIELDS } from '../cpg/handlers/cpg-xwalk.contract'
export { CPG_AUDIT_QUERY_FIELDS } from '../cpg/handlers/cpg-audit.contract'
export { CPG_PROPOSAL_QUERY_FIELDS } from '../cpg/handlers/cpg-proposals.contract'

export { CHART_RECORD_REMOVAL_REASONS } from '../chart-records/handlers/chart-records.contract'

// Artifact-special schemas shared with the web specials renderer (the wire
// `specials` field is loose; web narrows a special with these strict schemas
// before rendering it).
export {
  FOLLOWUP_VISIT_TOPIC_CATEGORIES,
  FOLLOWUP_VISIT_TOPICS_VERSION,
  followupVisitTopicSchema,
  followupVisitTopicsSpecialSchema,
  RISKS_GAPS_FINDINGS_VERSION,
  risksGapsFindingSchema,
  risksGapsFindingsSpecialSchema,
  visitTopicAnchor,
} from '../assistant/handlers/user-facing-entry.contract'
export type {
  FollowupVisitTopicWire,
  FollowupVisitTopicsSpecialWire,
  RisksGapsFindingWire,
  RisksGapsFindingsSpecialWire,
} from '../assistant/handlers/user-facing-entry.contract'

export const contract = {
  healthcheck,
  healthcheckUser,
  healthcheckAdmin,
  authLogin,
  authAdminOverview,
  authAdminSessions,
  authAdminRevokeSession,
  authAdminRevokeUserSessions,
  authAdminTrustedDevices,
  authAdminRevokeTrustedDevice,
  authAdminLockouts,
  authAdminClearLockout,
  authAdminChallenges,
  authAdminVoidChallenge,
  authAdminOtpOverrides,
  authAdminSetOtpOverride,
  authAdminRemoveOtpOverride,
  authAdminAuditEvents,
  authMfaSend,
  authMfaVerify,
  authProfileSelect,
  authResetPassword,
  authSetPassword,
  authChangePassword,
  authRefresh,
  authLogout,
  assistantActors,
  assistantConfig,
  assistantStart,
  assistantList,
  assistantGet,
  assistantStream,
  assistantDelete,
  assistantSend,
  assistantCancel,
  assistantFavoriteGet,
  assistantFavoriteAdd,
  assistantFavoriteUpdate,
  assistantFavoriteDelete,
  patientGet,
  patientHealthRecordList,
  patientHealthRecordGet,
  patientProfileGet,
  patientProfileUpdate,
  patientIdentityDocumentGet,
  patientIdentityDocumentUpload,
  listPatients,
  getPortalAccess,
  markIdentityVerified,
  getIdentityVerification,
  checkIdentityVerification,
  getPersonaIdImage,
  setPersonaEnrollment,
  inviteOrgMember,
  updatePractitionerCredentials,
  getPractitioner,
  getMyPractitionerOpsScope,
  getMyProfile,
  updateMyProfile,
  updateMyCredentials,
  listPractitioners,
  patientClinicalSummaryDetails,
  patientClinicalSummaryOverview,
  patientClinicalSummaryOverviewRefresh,
  patientRisksGaps,
  patientRisksGapsRefresh,
  listIncomingFaxes,
  getIncomingFax,
  markIncomingFaxViewed,
  deleteIncomingFax,
  triageIncomingFaxDocument,
  getIncomingFaxPatients,
  checkIncomingFaxPatients,
  reviewIncomingFaxPatients,
  triageIncomingFaxSplit,
  createFaxTask,
  detachFaxTasks,
  faxTaskSummaries,
  linkOutgoingFax,
  unlinkOutgoingFax,
  outgoingFaxLinks,
  updateOutgoingFaxLink,
  acknowledgeThreadDelivery,
  createOrganizationConnect,
  listOrganizationConnects,
  getOrganizationConnect,
  acceptOrganizationConnect,
  declineOrganizationConnect,
  deleteOrganizationConnect,
  transferAppointment,
  getSlotsByOrganization,
  listPatientVisits,
  cancelPatientVisit,
  patientVisitCallAccess,
  savePatientVisitLocation,
  savePatientVisitDeviceCheck,
  listPatientCoverages,
  submitPatientInsurance,
  uploadPatientInsuranceCard,
  savePatientVisitInsurance,
  insuranceVerdicts,
  getFutureAppointmentsByPatient,
  getLastSeenProvider,
  batchRemoveOrgMembers,
  removeOrgMember,
  getReturningPatientStatuses,
  getAppointmentTypes,
  listBookableProviders,
  getProviderDaySlots,
  listAppointmentAgenda,
  getAppointmentReadiness,
  consentGenerate,
  consentSign,
  consentGet,
  consentPreview,
  facilitySearch,
  facilityDiscoverySearch,
  facilityDiscoveryAdopt,
  facilityDiscoveryEnrichment,
  facilityDiscoveryFrequent,
  facilityDiscoveryRemember,
  facilityDiscoveryBookmark,
  referralDiscoveryNearby,
  referralDiscoveryRosters,
  referralDiscoveryDoctors,
  referralDiscoveryFrequent,
  referralDiscoveryResolve,
  referralDiscoveryRemember,
  referralDiscoveryBookmark,
  referralDraft,
  recordsFacilityLink,
  recordsFacilityCreate,
  recordLocationsGet,
  authorizationGenerate,
  authorizationSign,
  authorizationGet,
  getRecordLocatorEnrichment,
  runRecordLocatorEnrichment,
  refreshRecordLocatorProviders,
  scribeStartSession,
  scribeAppendTranscript,
  scribeFinalizeSession,
  scribeGetActiveSession,
  scribeGetSessionChunks,
  scribeGenerationStatus,
  scribeDiscardSession,
  scribeGenerateDraft,
  visitCallSignature,
  scribeJoinClaim,
  scribeJoinRelease,
  scribeRecordDuration,
  scribeDictateFormat,
  visitLockProgressNote,
  visitGetProgressNote,
  visitBillingGet,
  progressNoteSaveAddendum,
  progressNoteSignAddendum,
  patientExtractIdPhoto,
  patientFindDuplicate,
  patientEnrol,
  visitUpdateBilling,
  visitVideoToken,
  visitJoinLinkCreate,
  visitSubmitSoapNote,
  visitSaveSoapNoteDraft,
  tefcaRetrySearch,
  tefcaManualLink,
  tefcaManualCreate,
  listOrganizationAgreements,
  createOrganizationAgreement,
  terminateOrganizationAgreement,
  setOrganizationStripeKeys,
  getOrganizationStripeStatus,
  setOrganizationDoseSpotClinic,
  getOrganizationDoseSpotStatus,
  setPractitionerDoseSpotClinician,
  getPersonaPilot,
  enrollPersonaPilotPatient,
  removePersonaPilotPatient,
  reconcilePersonaAttempts,
  getMyIdentityVerification,
  giveMyIdentityVerificationConsent,
  startMyIdentityVerification,
  checkMyIdentityVerification,
  requestMyManualIdentityVerification,
  setOrganizationBridgeKey,
  getOrganizationBridgeStatus,
  listOrganizationBridgeProviders,
  setPractitionerBridgeProvider,
  setOrganizationStediKey,
  getOrganizationStediStatus,
  listOrganizationStediProviders,
  setPractitionerStediProvider,
  listUnmatchedPatientOrganizations,
  assignUnmatchedPatient,
  doseSpotLaunch,
  doseSpotLaunchNotifications,
  doseSpotSyncPrescriptions,
  doseSpotPharmacySearch,
  kateSessionCreate,
  kateSessionGet,
  kateSessionEnd,
  kateSessionTurnReport,
  kateSessionEventReport,
  kateCheckInGet,
  kateCheckInSend,
  kateCheckInReview,
  kateCheckInOpened,
  doseSpotSetDefaultPharmacy,
  doseSpotRemoveDefaultPharmacy,
  createCopayCheckout,
  getCopayStatus,
  syncCopayStatus,
  sendCopayLink,
  refundCopay,
  recordPatientPayment,
  resendCopayReceipt,
  patientSelfPayFee,
  patientSelfPayCheckout,
  patientSelfPayStatus,
  boardList,
  boardCreate,
  boardGet,
  boardRename,
  boardDelete,
  boardAddColumn,
  boardRenameColumn,
  boardReorderColumns,
  boardRemoveColumn,
  boardItemCreate,
  boardItemUpdate,
  boardItemDelete,
  boardNoteAdd,
  boardMarkRead,
  boardItemChecklistToggle,
  patientCareTasks,
  briefingDay,
  briefingItemState,
  boardAssignees,
  boardPatientSearch,
  careTaskConfigGet,
  careTaskConfigSet,
  boardNotifyPrefsList,
  boardNotifyPrefsSet,
  sharedTestPanelsGet,
  orderFaxStatus,
  seedSharedPanels,
  notificationList,
  notificationMarkRead,
  notificationDelete,
  notificationMarkAllRead,
  billingSummaries,
  billingDetail,
  billingSave,
  billingQuoteCharge,
  billingIcd10Billability,
  billingFeePolicyGet,
  superSetFeePolicy,
  superSeedFeeSchedule,
  billingV2CaseByEncounter,
  billingV2UpdateCase,
  billingV2SyncCase,
  billingV2ListCases,
  billingV2CaseDetail,
  billingV2UploadEob,
  billingV2RecordPayment,
  billingV2VccContext,
  billingV2VccCharge,
  billingSettingsStatus,
  billingSettingsUpdate,
  billingV2SubmitClaim,
  billingV2SetRoute,
  billingV2Report,
  billingV2ReportFilterOptions,
  billingV2ReportExport,
  boardTrashList,
  boardRestore,
  boardPurge,
  boardAdminList,
  boardOwnerCandidates,
  boardSetOwner,
  listTasks,
  getTask,
  runTask,
  updateTask,
  listTaskRuns,
  getTaskRun,
  taskRunAction,
  listCpgSources,
  listCpgDocuments,
  getCpgDocument,
  cpgDocumentFamilyAction,
  activateCpgDocument,
  listCpgFamilies,
  createCpgFamily,
  updateCpgFamily,
  listCpgMeasures,
  createCpgMeasure,
  updateCpgMeasure,
  listCpgXwalkRules,
  createCpgXwalkRule,
  deleteCpgXwalkRule,
  listCpgAudit,
  listCpgProposals,
  listCpgProposalClusters,
  acceptCpgProposalCluster,
  dismissCpgProposalCluster,
  chartRecordRemove,
  chartRecordRestore,
  chartRecordActivity,
  chartRecordsCombinedPdf,
  auditListAccessEvents,
  auditListChangeEvents,
  auditListFilterOptions,
  auditReportView,
  auditReportAction,
  careJourneys,
  createPatientLetter,
  letterSignatureRequestCreate,
  letterSignatureRequestList,
  letterSignatureRequestGet,
  letterSignatureRequestApprove,
  letterSigners,
  zocdocTestConnection,
  zocdocPingRead,
  zocdocConfig,
  zocdocProviderLinks,
  zocdocSyncState,
  zocdocSetSyncControls,
  zocdocProviderDetails,
  zocdocPractitionerCandidates,
  zocdocPractitionerSearch,
  zocdocLinkPractitioner,
  zocdocUnlinkPractitioner,
  zocdocLinkAppointment,
  zocdocImportAppointment,
  zocdocCancelAppointment,
  zocdocConfirmAppointment,
  zocdocRescheduleOptions,
  zocdocRescheduleAppointment,
  zocdocAppointmentStatus,
  zocdocFetchAppointments,
  zocdocSetAvailability,
  zocdocProviderSlots,
  zocdocRepublishAvailability,
  zocdocCreateAppointment,
  zocdocVisitReasons,
  patientCareJourney,
  patientLabOrder,
  patientMeClinicalSummaryOverview,
  patientMeTimeline,
  populationSummary,
  populationFamilies,
  populationMeasures,
  populationTrend,
  populationProviders,
  populationStates,
  populationMeasurePatients,
  populationFamilyPatients,
  populationCohortExport,
  crmLeadList,
  crmLeadSetStatus,
  crmLeadImportPatients,
  crmLeadSetNotes,
  crmLeadRecordManual,
  crmLeadCreate,
  crmLeadReport,
  crmLeadStatusBreakdown,
  crmCampaignList,
  crmCampaignGet,
  crmCampaignCreate,
  crmCampaignUpdate,
  crmCampaignLaunch,
  crmCohortList,
  crmCohortAdd,
  crmCohortRemove,
  crmCohortClear,
  crmLeadCampaigns,
  crmPopulationCohorts,
  crmPopulationCohortCount,
  crmCohortAddPopulation,
  patientMeMetricTrends,
  patientMeSchedulingCoverage,
  duplicateReviewList,
  duplicateReviewCounts,
  duplicateReviewOpen,
  duplicateReviewGet,
  duplicateReviewDecide,
  duplicateReviewForPatient,
  patientMerge,
  patientMergeGet,
  patientMergeResume,
  patientMergeRedirect,
  mergedPatientList,
  patientMergeRestore,
  patientMergeSettingsGet,
  patientMergeSettingsSet,
} as const

// Kate pre-visit checklist vocabulary shared with patient-web (the PreVisit
// page's live checklist + status polling) via `@behuman/core-contract`.
export {
  kateAppointmentOutcomeSchema,
  kateChecklistSchema,
  kateLanguageSchema,
  kateSessionStatusSchema,
} from '../kate/handlers/kate-session.contract'
export type {
  KateAppointmentOutcome,
  KateChecklist,
  KateLanguage,
  KateSessionStatus,
} from '../kate/handlers/kate-session.contract'

/**
 * The chart sections whose resource types back Change History, exported so `web` can hold
 * this map against its own section registry instead of keeping a hand-copy of it.
 *
 * QA round 2 found four chart sections (care-coordination notes, procedures, immunizations,
 * family history) whose changes were never derived, because this mapping lives in middleware
 * while the registry that decides what a section contains lives in `web`, and nothing
 * compared them. `auditChangeCoverage.test.ts` now does, so adding a section without
 * deciding its audit story fails the build. A real import and not a mirror: a mirror is a
 * copy, and a copy is what drifted.
 *
 * It lives in `audit.contract.ts` rather than beside the server-only section helpers
 * because a Vite plugin fails the frontend builds when a middleware module that is not a
 * `*.contract.ts` is pulled across the package boundary — the same rule AGENTS.md states,
 * enforced at build time.
 */

/**
 * The CLOSED vocabulary the view-reporting endpoint accepts, exported for the same reason
 * and to the same gate: `auditChangeCoverage.test.ts` holds the chart's section registry
 * against it, so a new section whose key nobody added here fails the build instead of
 * failing its own view reports at runtime — see the constant's own comment for why a 400
 * would be the worse of the two outcomes.
 */
export { AUDIT_SECTION_KEYS, isAuditSectionKey } from '../audit/handlers/audit.contract'

/**
 * The CLOSED vocabulary the view-reporting endpoint accepts, exported for the same reason
 * and to the same gate: `auditChangeCoverage.test.ts` holds the chart's section registry
 * against it, so a new section whose key nobody added here fails the build instead of
 * failing its own view reports at runtime — see the constant's own comment for why a 400
 * would be the worse of the two outcomes.
 */
export type { AuditSectionKey } from '../audit/handlers/audit.contract'
